import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";

export default function TwoFactorSection({ onEnabled }) {
  const { t } = useTranslation("common");
  const { setup2fa, enable2fa, disable2fa, regenerateBackupCodes, get2faStatus } = useAuth();
  const [status, setStatus] = useState(null);         // { enabled, backup_codes_remaining }
  const [phase, setPhase] = useState("idle");         // idle | setup | codes
  const [setupData, setSetupData] = useState(null);   // { qr_data_uri, secret_base32 }
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = async () => { try { setStatus(await get2faStatus()); } catch { /* ignore */ } };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("settings.security.two_factor.toast.error"));

  const startSetup = async () => {
    setBusy(true);
    try { setSetupData(await setup2fa()); setPhase("setup"); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const confirmEnable = async () => {
    setBusy(true);
    try {
      const res = await enable2fa({ code: code.trim() });
      setCodes(res.backup_codes); setPhase("codes"); setCode("");
      toast.success(t("settings.security.two_factor.toast.enabled"));
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doDisable = async () => {
    setBusy(true);
    try {
      await disable2fa({ password }); setPassword("");
      toast.success(t("settings.security.two_factor.toast.disabled")); await refresh();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const doRegenerate = async () => {
    setBusy(true);
    try {
      const res = await regenerateBackupCodes({ password }); setPassword("");
      setCodes(res.backup_codes); setPhase("codes");
      toast.success(t("settings.security.two_factor.toast.regenerated")); await refresh();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const finishCodes = async () => {
    setCodes([]); setPhase("idle"); await refresh();
    if (typeof onEnabled === "function") onEnabled();
  };
  const copyCodes = () => navigator.clipboard?.writeText(codes.join("\n"));

  const T = (k, o) => t(`settings.security.two_factor.${k}`, o);

  return (
    <section className="border border-zinc-200 rounded-2xl bg-white p-6 mb-6" data-testid="twofa-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-100 text-zinc-700">
          <ShieldCheck size={18} strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-950">{T("title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-md">{T("subtitle")}</p>
        </div>
      </div>

      {phase === "idle" && (
        <div data-testid="twofa-status">
          <p className="text-sm text-zinc-700 mb-3">
            {status?.enabled ? T("status_on") : T("status_off")}
            {status?.enabled && <> · {T("remaining", { count: status.backup_codes_remaining })}</>}
          </p>
          {!status?.enabled ? (
            <Button onClick={startSetup} disabled={busy} data-testid="twofa-enable-button"
              className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg">
              {busy ? <Loader2 size={14} className="animate-spin" /> : T("enable_cta")}
            </Button>
          ) : (
            <div className="space-y-3">
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder={T("password_prompt")} data-testid="twofa-password-input"
                className="w-full max-w-xs h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300" />
              <div className="flex gap-2">
                <Button onClick={doDisable} disabled={busy || !password} data-testid="twofa-disable-button"
                  className="h-10 bg-white border border-zinc-200 text-zinc-950 hover:bg-zinc-50 rounded-lg">
                  {T("disable_cta")}
                </Button>
                <Button onClick={doRegenerate} disabled={busy || !password} data-testid="twofa-regenerate-button"
                  className="h-10 bg-white border border-zinc-200 text-zinc-950 hover:bg-zinc-50 rounded-lg">
                  {T("regenerate_cta")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {phase === "setup" && setupData && (
        <div className="space-y-3" data-testid="twofa-setup">
          <p className="text-sm text-zinc-600">{T("scan_hint")}</p>
          <img src={setupData.qr_data_uri} alt="2FA QR" className="w-40 h-40" data-testid="twofa-qr" />
          <p className="text-xs text-zinc-500">{T("manual_secret_label")}</p>
          <code className="block font-mono text-sm text-zinc-800 break-all" data-testid="twofa-secret">{setupData.secret_base32}</code>
          <label className="text-xs text-zinc-500 block">{T("confirm_code_label")}</label>
          <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)}
            data-testid="twofa-confirm-input"
            className="w-full max-w-xs h-10 px-3 rounded-lg border border-zinc-200 text-sm tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-zinc-300" />
          <Button onClick={confirmEnable} disabled={busy || code.length < 6} data-testid="twofa-confirm-button"
            className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg">{T("confirm_cta")}</Button>
        </div>
      )}

      {phase === "codes" && (
        <div className="space-y-3" data-testid="twofa-backup-codes">
          <h3 className="font-semibold text-zinc-950">{T("backup_codes_title")}</h3>
          <p className="text-xs text-rose-700">{T("backup_codes_warning")}</p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-zinc-800 bg-zinc-50 rounded-lg p-3">
            {codes.map((c) => <li key={c} data-testid="twofa-backup-code">{c}</li>)}
          </ul>
          <div className="flex gap-2">
            <Button onClick={copyCodes} data-testid="twofa-copy-button"
              className="h-9 bg-white border border-zinc-200 text-zinc-950 hover:bg-zinc-50 rounded-lg">{T("copy")}</Button>
            <Button onClick={finishCodes} data-testid="twofa-done-button"
              className="h-9 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg">{T("done")}</Button>
          </div>
        </div>
      )}
    </section>
  );
}
